import { useState, useEffect, useCallback } from "react";
import apiClient from "../../api/axios";
import { ExtractedContract } from "../../types/contract";
import { FlowState } from "./types";

export const useDetailedJob = (
  flowState: FlowState,
  setFlowState: (state: FlowState) => void,
  setFinalContracts: (updater: (prev: ExtractedContract[]) => ExtractedContract[]) => void,
  POLLING_INTERVAL_DETAILED_MS: number,
  POLLING_JITTER_MS: number
) => {
  // 詳細抽出ポーリング用のState
  const [detailedJobIds, setDetailedJobIds] = useState<string[]>([]);
  const [detailedJobStatuses, setDetailedJobStatuses] = useState<
    Record<string, { status: "processing" | "complete" | "error" }>
  >({});
  const [changedContractIds, setChangedContractIds] = useState<Set<number>>(new Set());

  // 詳細抽出ジョブのポーリング処理
  useEffect(() => {
    if (flowState !== "detailedProcessing") {
      return;
    }

    let activeTimeouts: Record<string, NodeJS.Timeout> = {};

    const scheduleDetailedPoll = async (jobId: string, initialDelay = 0) => {
      const jitter = Math.random() * POLLING_JITTER_MS;
      const delay = initialDelay + jitter;

      activeTimeouts[jobId] = setTimeout(async () => {
        try {
          const response = await apiClient.get(
            `/api/detailed-job-status/${jobId}`
          );
          if (response.data.status === "complete") {
            setDetailedJobStatuses((prev) => ({
              ...prev,
              [jobId]: { ...prev[jobId], status: "complete" },
            }));
            setFinalContracts((prev) => [...prev, response.data.data]);
            if (activeTimeouts[jobId]) clearTimeout(activeTimeouts[jobId]);
            delete activeTimeouts[jobId];
          } else {
            scheduleDetailedPoll(jobId, POLLING_INTERVAL_DETAILED_MS);
          }
        } catch (err) {
          console.error(`Error polling detailed job ${jobId}:`, err);
          setDetailedJobStatuses((prev) => ({
            ...prev,
            [jobId]: { ...prev[jobId], status: "error" },
          }));
          if (activeTimeouts[jobId]) clearTimeout(activeTimeouts[jobId]);
          delete activeTimeouts[jobId];
        }
      }, delay);
    };

    // Initial scheduling for all processing jobs
    Object.keys(detailedJobStatuses).forEach((jobId) => {
      if (detailedJobStatuses[jobId].status === "processing" && !activeTimeouts[jobId]) {
        // Only for the very first poll, introduce a 30-second delay.
        // Subsequent polls will use POLLING_INTERVAL_DETAILED_MS.
        scheduleDetailedPoll(jobId, 30000); // 30 seconds initial delay
      }
    });

    const allDetailedJobsDone = Object.values(detailedJobStatuses).every(
      (job) => job.status === "complete" || job.status === "error"
    );

    if (allDetailedJobsDone && flowState === "detailedProcessing") {
      setFlowState("detailedExtracted");
    }

    return () => {
      // Cleanup: Clear all timeouts if component unmounts or dependencies change
      Object.values(activeTimeouts).forEach(clearTimeout);
    };
  }, [flowState, detailedJobStatuses, setFlowState, setFinalContracts, POLLING_INTERVAL_DETAILED_MS, POLLING_JITTER_MS]);

  // リセット関数
  const resetDetailedJobState = useCallback(() => {
    setDetailedJobIds([]);
    setDetailedJobStatuses({});
    setChangedContractIds(new Set());
  }, []);

  return {
    detailedJobIds,
    setDetailedJobIds,
    detailedJobStatuses,
    setDetailedJobStatuses,
    changedContractIds,
    setChangedContractIds,
    resetDetailedJobState,
  };
};